import type {
  ElementBlock,
  EquipmentItemType,
  EquipmentSkill,
  EquipmentSkillContent,
  EquipmentSkillContentTechnique,
  EquipmentSkillTechniqueStatusEffectApplication,
  GameElement,
  GameStat,
  SkillStatBonus,
  SkillStatScaling,
} from '@interfaces';
import { SKILL_MAX_ALLY_TARGETS } from '@helpers/config';
import { getEntriesByType } from '@helpers/content/content';
import { ensureElementBlock } from '@helpers/content/ensure-helpers-stats';
import { GameElementOrder, ROMAN_NUMERAL_TIERS, StatOrder } from '@interfaces';

import { clamp, uniq } from 'es-toolkit/compat';

// Heroes need one of requiredWeaponTypes equipped (empty = no requirement); monsters never carry equipment.
export function skillIsUsableWithEquippedWeapons(
  skill: EquipmentSkill,
  equippedWeaponTypes: EquipmentItemType[],
): boolean {
  if (skill.requiredWeaponTypes.length === 0) return true;
  return skill.requiredWeaponTypes.some((type) =>
    equippedWeaponTypes.includes(type),
  );
}

export function skillUses(skill: EquipmentSkill): number {
  return skill.usesPerCombat + (skill.mods?.usesPerCombat ?? 0);
}

export function skillEpCost(skill: EquipmentSkill): number {
  return skill.epCost + (skill.mods?.epCost ?? 0);
}

// Skill copies stored in pre-existing mid-combat saves may lack these fields.
export function skillIsSpecial(skill: EquipmentSkill): boolean {
  return skill.special ?? false;
}

export function skillCooldown(skill: EquipmentSkill): number {
  return skill.cooldown ?? 0;
}

export function skillElementCosts(skill: EquipmentSkill): ElementBlock {
  return ensureElementBlock(skill.elementCosts);
}

export function skillHasElementCosts(skill: EquipmentSkill): boolean {
  const costs = skillElementCosts(skill);
  return GameElementOrder.some((element) => costs[element] > 0);
}

export function skillTechniqueNumTargets(
  skill: EquipmentSkill,
  technique: EquipmentSkillContentTechnique,
): number {
  const numTargets = technique.targets;
  if (technique.targetType === 'Self') return 1;
  if (technique.targetType === 'Allies') {
    return clamp(numTargets, 1, SKILL_MAX_ALLY_TARGETS);
  }
  return numTargets;
}

export function skillTechniqueDamageScalingStat(
  skill: EquipmentSkill,
  technique: EquipmentSkillContentTechnique,
  stat: GameStat,
): number {
  return technique.damageScaling[stat];
}

export function skillTechniqueStatScaling(
  technique: EquipmentSkillContentTechnique,
): SkillStatScaling[] {
  return StatOrder.filter((stat) => technique.damageScaling[stat]).map(
    (stat) => ({ stat, multiplier: technique.damageScaling[stat] }),
  );
}

// Only techniques that already scale off a stat gain the bonus, so buff/debuff-only techniques are untouched.
export function skillTechniqueWithStatBonuses(
  skill: EquipmentSkill,
  technique: EquipmentSkillContentTechnique,
  bonuses: SkillStatBonus[] = [],
): EquipmentSkillContentTechnique {
  const applicable = bonuses.filter(
    (bonus) => bonus.skillFamily === skill.family,
  );
  if (
    applicable.length === 0 ||
    skillTechniqueStatScaling(technique).length === 0
  ) {
    return technique;
  }

  const damageScaling = { ...technique.damageScaling };
  applicable.forEach(({ stat, value }) => {
    damageScaling[stat] = Math.max(0, (damageScaling[stat] ?? 0) + value);
  });

  return { ...technique, damageScaling };
}

export function skillTechniqueStatusEffectChance(
  skill: EquipmentSkill,
  techniqueApplication: EquipmentSkillTechniqueStatusEffectApplication,
): number {
  return (
    techniqueApplication.chance +
    (skill.mods?.statusEffectChanceBoost?.[
      techniqueApplication.statusEffectId
    ] ?? 0)
  );
}

export function skillTechniqueStatusEffectDuration(
  skill: EquipmentSkill,
  techniqueApplication: EquipmentSkillTechniqueStatusEffectApplication,
): number {
  return (
    techniqueApplication.duration +
    (skill.mods?.statusEffectDurationBoost?.[
      techniqueApplication.statusEffectId
    ] ?? 0)
  );
}

export function skillElements(skill: EquipmentSkill): GameElement[] {
  return uniq(skill.techniques.flatMap((t) => t.elements)).sort();
}

// The pool gains one charge per element per cast, credited to the first technique carrying it.
export function skillTechniqueGeneratedElements(
  skill: EquipmentSkill,
): GameElement[][] {
  if (skillHasElementCosts(skill)) return skill.techniques.map(() => []);

  const seen = new Set<GameElement>();
  return skill.techniques.map((technique) =>
    uniq(technique.elements).filter((element) => {
      if (seen.has(element)) return false;
      seen.add(element);
      return true;
    }),
  );
}

// Splits a display name into upgrade family and rank, e.g. "Starshine II" -> { family: 'Starshine', tier: 2 }.
export function skillNameTier(name: string): { family: string; tier: number } {
  const match = name.match(/^(.*) (I{1,3}|IV|V|VI{1,3}|IX|X)$/);
  if (!match) return { family: name, tier: 1 };

  return { family: match[1], tier: ROMAN_NUMERAL_TIERS[match[2]] };
}

// Upgrades a known same-family skill to the granted tier, drops it if a same-or-higher tier is already known, else appends.
function applyGrantedSkill(
  skills: EquipmentSkillContent[],
  granted: EquipmentSkillContent,
): EquipmentSkillContent[] {
  const grantedTier = skillNameTier(granted.name);
  const existingIndex = skills.findIndex(
    (skill) => skillNameTier(skill.name).family === grantedTier.family,
  );

  if (existingIndex === -1) return [...skills, granted];

  const existingTier = skillNameTier(skills[existingIndex].name);
  if (existingTier.tier >= grantedTier.tier) return skills;

  return skills.map((skill, i) => (i === existingIndex ? granted : skill));
}

// Merges equipment-granted skills into a hero's base (job-path) skills.
export function mergeGrantedSkills(
  baseSkills: EquipmentSkillContent[],
  grantedSkills: EquipmentSkillContent[],
): EquipmentSkillContent[] {
  let merged = baseSkills;

  grantedSkills.forEach((granted) => {
    merged = applyGrantedSkill(merged, granted);
  });

  return merged;
}

// Any skill in the family supplies the icon.
export function skillFamilyEntry(
  family: string,
): EquipmentSkillContent | undefined {
  return getEntriesByType<EquipmentSkillContent>('skill').find(
    (skill) => skill.family === family,
  );
}
